import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import styles from "./WheelSpinTab.module.css";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Icon } from "@iconify/react";
import {
  getWheelPoint,
  getPlayerBalance,
  getUsersByID,
} from "../../store/slices/user";

const MAX_WHEEL_PRIZES = 10;
const sliceColors = [
  "#ff006e",
  "#ffbe0b",
  "#fb5607",
  "#8338ec",
  "#3a86ff",
  "#c02b58",
  "#20bf55",
  "#ff9f1c",
  "#7209b7",
  "#06d6a0",
];

const SuccessToast = ({ message, show, onClose }) => {
  useEffect(() => {
    if (!show) return;
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        backgroundColor: "#D1FAE5",
        border: "2px solid #10B981",
        borderRadius: "12px",
        padding: "16px 20px",
        maxWidth: "320px",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
        zIndex: 10000,
        animation: "slideIn 0.3s ease-out",
        display: "flex",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <span style={{ fontSize: "20px" }}>✅</span>
      <span style={{ color: "#065F46", fontWeight: "500", flex: 1 }}>
        {message}
      </span>
      <button
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          fontSize: "24px",
          cursor: "pointer",
          color: "#065F46",
          padding: "4px",
          marginLeft: "8px",
          borderRadius: "50%",
          width: "32px",
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.1)";
          e.currentTarget.style.transform = "scale(1.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        ×
      </button>
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

const WheelSpinTab = () => {
  const dispatch = useDispatch();
  const { singleUsers, wheelPoint } = useSelector((s) => s.user);
  const user = singleUsers?.[0];
  // Block spin when user is under withdraw condition or has active promotion
  const withdrawConditionDetail = user?.withdrawConditionDetail;
  const promotionStatus = user?.PromotionStatus;
  const isPromoActive = promotionStatus === "ACTIVE";

  const [settings, setSettings] = useState(null);
  const [prizes, setPrizes] = useState(() => Array(MAX_WHEEL_PRIZES).fill(0));
  const [probabilities, setProbabilities] = useState(() =>
    Array(MAX_WHEEL_PRIZES).fill(0),
  );
  const [deg, setDeg] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "" });
  const rotationRef = useRef(0);

  const sectorAngle = useMemo(
    () => (prizes.length ? 360 / prizes.length : 0),
    [prizes.length],
  );

  const showSuccessToast = (message) => setToast({ show: true, message });
  const hideToast = () => setToast({ show: false, message: "" });

  const fetchSettings = useCallback(async () => {
    try {
      setError("");
      const res = await axios.get(
        "https://api.pgzone88.com/setting/wheelsetting",
      );
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      const first = list[0] || {};
      setSettings(first);
      const prizeData = Array.from({ length: MAX_WHEEL_PRIZES }, (_, idx) => {
        const key = idx + 1;
        return Number(first?.[`prizeAmount${key}`]) || 0;
      });
      const probData = Array.from({ length: MAX_WHEEL_PRIZES }, (_, idx) => {
        const key = idx + 1;
        const value = Number(first?.[`prizeProb${key}`]) || 0;
        return value / 100;
      });
      setPrizes(prizeData);
      setProbabilities(probData);
    } catch (e) {
      setError("โหลดการตั้งค่ากงล้อล้มเหลว ใช้ค่าเริ่มต้น");
      setPrizes([0, 5, 10, 15, 20, 30, 45, 60, 80, 100]);
      setProbabilities([
        0.25, 0.18, 0.14, 0.1, 0.08, 0.07, 0.06, 0.05, 0.04, 0.03,
      ]);
    }
  }, []);

  // Helper to normalize user's available points from various possible shapes
  const getUserPoints = useCallback(() => {
    const src = wheelPoint;
    if (typeof src === "number") return src;
    if (typeof src === "string" && !Number.isNaN(Number(src)))
      return Number(src);
    const item = Array.isArray(src) ? src[0] : src || {};
    const candidates = [
      item?.wheelPoint,
      item?.point,
      item?.points,
      item?.diamond,
      item?.balance,
    ];
    for (const v of candidates) {
      const n = typeof v === "string" ? Number(v) : v;
      if (typeof n === "number" && !Number.isNaN(n)) return n;
    }
    return 0;
  }, [wheelPoint]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);
  useEffect(() => {
    if (user?.id) dispatch(getWheelPoint(user.id));
  }, [user?.id, dispatch]);

  const spinAllowed = () => {
    if (!user || !settings) return false;
    // Disallow spin when on withdraw condition or promotion is ACTIVE
    if (withdrawConditionDetail) return false;
    if (isPromoActive) return false;
    const cost = Number(settings?.spinAmount ?? settings?.spinCost ?? 0);
    const current = getUserPoints();
    const hasProbability = probabilities.some((prob) => prob > 0);
    return current >= cost && !spinning && hasProbability;
  };

  const spin = async () => {
    if (!spinAllowed()) return;
    setSpinning(true);

    try {
      setError("");
      const response = await axios.post(
        `https://api.pgzone88.com/wheel/spining/${user.id}`,
        {},
      );
      const prizeIndex = Number(response?.data?.prizeIndex);
      const prizeAmount = Number(response?.data?.prize);

      if (
        !Number.isInteger(prizeIndex) ||
        prizeIndex < 0 ||
        prizeIndex >= prizes.length
      ) {
        throw new Error("invalid-prize-index");
      }

      const spins = Math.floor(Math.random() * 6) + 10;
      const currentRotation = rotationRef.current;
      const normalizedRotation = ((currentRotation % 360) + 360) % 360;
      const targetRotation = prizeIndex * sectorAngle;
      let deltaRotation = targetRotation - normalizedRotation;

      if (deltaRotation < 0) {
        deltaRotation += 360;
      }

      const jitter = (Math.random() - 0.5) * Math.min(sectorAngle * 0.6, 18);
      const totalRotation = 360 * spins + deltaRotation + jitter;

      setDeg((current) => {
        const nextRotation = current + totalRotation;
        rotationRef.current = nextRotation;
        return nextRotation;
      });

      setTimeout(() => {
        if (prizeAmount > 0) {
          showSuccessToast(`ยินดีด้วย! คุณได้รับ ${prizeAmount}฿`);
        }
        if (user?.id) {
          dispatch(getWheelPoint(user.id));
          dispatch(getPlayerBalance(user));
          dispatch(getUsersByID(user));
        }
        setSpinning(false);
      }, 5200);
    } catch (_) {
      setError("หมุนวงล้อไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      setSpinning(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.iconWrapper}>
          <Icon icon="mdi:progress-clock" className={styles.icon} />
        </div>
        <h2 className={styles.title}>กงล้อพารวย</h2>
        <p className={styles.subtitle}>สะสมเพชรเพื่อหมุนรับของรางวัล</p>
        {error && <div className={styles.errorMsg}>{error}</div>}
      </div>

      <div className={styles.wheelSection}>
        <div className={styles.wheelOuter}></div>
        <div className={styles.wheel}>
          <div
            className={styles.inner}
            style={{ transform: `rotate(${deg}deg)` }}
          >
            {prizes.map((p, i) => (
              <div
                key={`slice-${i}`}
                className={styles.slice}
                style={{
                  transform: `rotate(${i * -sectorAngle}deg)`,
                  borderColor: `${
                    sliceColors[i % sliceColors.length]
                  } transparent`,
                }}
              >
                <span className={styles.prize}>
                  {Number(p) || Number(p) === 0 ? `${p}฿` : "-"}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.arrow}></div>
        <button
          className={styles.spinButton}
          onClick={spin}
          disabled={!spinAllowed()}
        >
          {spinning ? "กำลังหมุน..." : "หมุน"}
        </button>
      </div>

      <div className={styles.statusRow}>
        <div className={styles.statusBox}>
          <span className={styles.statusLabel}>แต้มเพชร</span>
          <span className={styles.statusValue}>{getUserPoints()}</span>
        </div>
        <div className={styles.statusBox}>
          <span className={styles.statusLabel}>หมุนครั้งละ</span>
          <span className={styles.statusValue}>
            {Number(settings?.spinAmount ?? settings?.spinCost ?? 0)}
          </span>
        </div>
        <div className={styles.statusBox}>
          <span className={styles.statusLabel}>สถานะ</span>
          <span className={styles.statusValue}>
            {spinAllowed() ? "พร้อมหมุน" : "ยังไม่พร้อม"}
          </span>
        </div>
      </div>

      <SuccessToast
        message={toast.message}
        show={toast.show}
        onClose={hideToast}
      />
    </div>
  );
};

export default WheelSpinTab;
